<template>
  <ContentWrap>
    <el-form :inline="true" class="-mb-15px">
      <el-form-item>
        <el-button @click="getList"><Icon class="mr-5px" icon="ep:refresh" />刷新</el-button>
      </el-form-item>
    </el-form>
  </ContentWrap>

  <ContentWrap>
    <el-table v-loading="loading" :data="list" stripe>
      <el-table-column label="ID" prop="id" width="80" />
      <el-table-column label="播主名称" min-width="140" show-overflow-tooltip>
        <template #default="{ row }">
          {{ row.anchorNickname || '—' }}
          <span class="text-gray-400 text-12px">（{{ row.anchorUserId }}）</span>
        </template>
      </el-table-column>
      <el-table-column label="直播时间" prop="liveTime" width="170" :formatter="dateFormatter" />
      <el-table-column label="直播间" min-width="160" show-overflow-tooltip>
        <template #default="{ row }">
          {{ row.liveTitle || '—' }}
          <span class="text-gray-400 text-12px">（{{ row.liveId }}）</span>
        </template>
      </el-table-column>
      <el-table-column label="成交数量" prop="orderCount" width="100" />
      <el-table-column label="订单金额(元)" min-width="120">
        <template #default="{ row }">
          {{ row.orderAmount != null ? row.orderAmount : '—' }}
        </template>
      </el-table-column>
      <el-table-column label="佣金(元)" min-width="110">
        <template #default="{ row }">
          {{ row.commissionAmount != null ? row.commissionAmount : '—' }}
        </template>
      </el-table-column>
      <el-table-column label="结算状态" width="100">
        <template #default="{ row }">
          <el-tag :type="statusTag(row.status)">{{ statusLabel(row.status) }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column label="关联时间" prop="createTime" width="170" :formatter="dateFormatter" />
      <el-table-column label="操作" width="180" fixed="right">
        <template #default="{ row }">
          <el-button link type="primary" @click="openDetail(row.id)">查看</el-button>
          <el-button
            v-if="row.status === 0 && Number(row.commissionAmount) > 0"
            link
            type="success"
            @click="openSettle(row.id)"
            >登记结算</el-button
          >
        </template>
      </el-table-column>
    </el-table>
    <Pagination
      v-model:page="queryParams.pageNo"
      v-model:limit="queryParams.pageSize"
      :total="total"
      @pagination="getList"
    />
  </ContentWrap>

  <el-drawer v-model="detailVisible" title="结算详情" size="420px">
    <el-skeleton v-if="detailLoading" :rows="6" animated />
    <el-descriptions v-else-if="detail" :column="1" border>
      <el-descriptions-item label="结算单号">{{ detail.id }}</el-descriptions-item>
      <el-descriptions-item label="播主名称">
        {{ detail.anchorNickname || '—' }}（{{ detail.anchorUserId }}）
      </el-descriptions-item>
      <el-descriptions-item label="直播间">
        {{ detail.liveTitle || '—' }}（{{ detail.liveId }}）
      </el-descriptions-item>
      <el-descriptions-item label="直播时间">{{
        detail.liveTime ? formatDate(detail.liveTime) : '—'
      }}</el-descriptions-item>
      <el-descriptions-item label="成交数量">{{ detail.orderCount }}</el-descriptions-item>
      <el-descriptions-item label="订单金额">{{ detail.orderAmount }} 元</el-descriptions-item>
      <el-descriptions-item label="佣金">{{ detail.commissionAmount }} 元</el-descriptions-item>
      <el-descriptions-item label="结算状态">{{ statusLabel(detail.status) }}</el-descriptions-item>
      <el-descriptions-item v-if="detail.status === 1" label="结算方式"
        >线下转账登记</el-descriptions-item
      >
      <el-descriptions-item v-if="detail.paymentRef" label="转账流水号">{{
        detail.paymentRef
      }}</el-descriptions-item>
      <el-descriptions-item v-if="detail.paidAt" label="付款时间">{{
        formatDate(detail.paidAt)
      }}</el-descriptions-item>
      <el-descriptions-item v-if="detail.settledAt" label="登记时间">{{
        formatDate(detail.settledAt)
      }}</el-descriptions-item>
      <el-descriptions-item v-if="detail.settledBy" label="经办人编号">{{
        detail.settledBy
      }}</el-descriptions-item>
      <el-descriptions-item v-if="detail.proofUrl" label="转账凭证">
        <el-image
          :src="detail.proofUrl"
          :preview-src-list="[detail.proofUrl]"
          preview-teleported
          style="width: 120px; height: 90px"
          fit="contain"
        />
      </el-descriptions-item>
      <el-descriptions-item v-if="detail.settleRemark" label="备注">{{
        detail.settleRemark
      }}</el-descriptions-item>
      <el-descriptions-item label="关联时间">{{
        detail.createTime ? formatDate(detail.createTime) : '—'
      }}</el-descriptions-item>
    </el-descriptions>
    <el-empty v-else description="未找到结算单" />
  </el-drawer>
  <el-dialog
    v-model="settleVisible"
    title="登记线下佣金结算"
    width="520px"
    :close-on-click-modal="false"
    :before-close="closeSettle"
  >
    <el-alert
      title="请先完成向主播的线下转账。此处仅登记付款，不会发起转账或增加钱包余额。"
      type="info"
      :closable="false"
      class="mb-16px"
    />
    <el-form ref="settleFormRef" :model="settleForm" :rules="settleRules" label-width="100px">
      <el-form-item label="主播"
        >{{ settleRecord?.anchorNickname }}（{{ settleRecord?.anchorUserId }}）</el-form-item
      >
      <el-form-item label="佣金金额">{{ settleRecord?.commissionAmount }} 元</el-form-item>
      <el-form-item label="流水号" prop="paymentRef"
        ><el-input
          v-model="settleForm.paymentRef"
          maxlength="128"
          placeholder="银行或支付渠道的转账流水号"
          :disabled="saving"
      /></el-form-item>
      <el-form-item label="付款时间" prop="paidAt"
        ><el-date-picker
          v-model="settleForm.paidAt"
          type="datetime"
          value-format="YYYY-MM-DD HH:mm:ss"
          :disabled-date="futureDate"
          :disabled="saving"
          placeholder="实际付款时间"
      /></el-form-item>
      <el-form-item label="转账凭证" prop="proofFileId">
        <div>
          <el-upload
            :show-file-list="false"
            accept="image/jpeg,image/png"
            :http-request="uploadProof"
            :before-upload="beforeProofUpload"
            :disabled="saving || uploading"
          >
            <el-button :loading="uploading" :disabled="saving">{{
              settleForm.proofFileId ? '重新上传凭证' : '上传凭证'
            }}</el-button>
          </el-upload>
          <el-image
            v-if="proofUrl"
            :src="proofUrl"
            :preview-src-list="[proofUrl]"
            preview-teleported
            style="width: 160px; height: 100px; margin-top: 8px"
            fit="contain"
          />
          <div class="text-gray-400 text-12px">JPG / PNG，最大 5MB</div>
        </div>
      </el-form-item>
      <el-form-item label="备注"
        ><el-input v-model="settleForm.remark" type="textarea" maxlength="500" :disabled="saving"
      /></el-form-item>
      <el-form-item
        ><el-checkbox v-model="paymentConfirmed" :disabled="saving"
          >确认已向该主播支付上述佣金</el-checkbox
        ></el-form-item
      >
    </el-form>
    <template #footer>
      <el-button @click="settleVisible = false" :disabled="saving || uploading">取消</el-button>
      <el-button
        type="primary"
        :loading="saving"
        :disabled="uploading || !paymentConfirmed"
        @click="submitSettle"
        >确认登记结算</el-button
      >
    </template>
  </el-dialog>
</template>

<script setup lang="ts">
import * as LiveApi from '@/api/merchant/live'
import { dateFormatter, formatDate } from '@/utils/formatTime'
import { ElMessage, ElMessageBox } from 'element-plus'
import type { FormInstance, FormRules, UploadRequestOptions, UploadRawFile } from 'element-plus'

defineOptions({ name: 'MerchantLiveSettle' })

const loading = ref(false)
const list = ref<LiveApi.LiveSettleRecordVO[]>([])
const total = ref(0)
const queryParams = reactive({
  pageNo: 1,
  pageSize: 10
})

const detailVisible = ref(false)
const detailLoading = ref(false)
const detail = ref<LiveApi.LiveSettleRecordVO | null>(null)

const statusLabel = (status?: number) => {
  if (status === 0) return '待结算'
  if (status === 1) return '已结算'
  if (status === 2) return '已作废'
  return status != null ? String(status) : '—'
}

const statusTag = (status?: number) => {
  if (status === 0) return 'warning'
  if (status === 1) return 'success'
  if (status === 2) return 'info'
  return 'info'
}

const getList = async () => {
  loading.value = true
  try {
    const data = await LiveApi.getLiveSettlePage({
      pageNo: queryParams.pageNo,
      pageSize: queryParams.pageSize
    })
    list.value = data?.list || []
    total.value = data?.total || 0
  } finally {
    loading.value = false
  }
}

const openDetail = async (id: number) => {
  detailVisible.value = true
  detailLoading.value = true
  detail.value = null
  try {
    detail.value = await LiveApi.getLiveSettle(id)
  } finally {
    detailLoading.value = false
  }
}

const settleVisible = ref(false)
const settleRecord = ref<LiveApi.LiveSettleRecordVO | null>(null)
const saving = ref(false)
const uploading = ref(false)
const paymentConfirmed = ref(false)
const proofUrl = ref('')
const settleFormRef = ref<FormInstance>()
const settleForm = reactive({
  paymentRef: '',
  paidAt: '',
  proofFileId: undefined as number | undefined,
  remark: ''
})
const settleRules: FormRules = {
  paymentRef: [{ required: true, whitespace: true, message: '请填写转账流水号', trigger: 'blur' }],
  paidAt: [{ required: true, message: '请选择实际付款时间', trigger: 'change' }],
  proofFileId: [{ required: true, message: '请上传转账凭证', trigger: 'change' }]
}
const futureDate = (date: Date) => date.getTime() > Date.now()
const closeSettle = (done: () => void) => {
  if (!saving.value && !uploading.value) done()
}
const openSettle = async (id: number) => {
  if (saving.value || uploading.value) return
  const record = await LiveApi.getLiveSettle(id)
  if (!record || record.status !== 0 || Number(record.commissionAmount) <= 0) {
    ElMessage.warning('结算单状态已变化，请刷新')
    await getList()
    return
  }
  settleRecord.value = record
  Object.assign(settleForm, {
    paymentRef: '',
    paidAt: '',
    proofFileId: record.proofFileId,
    remark: ''
  })
  proofUrl.value = record.proofUrl || ''
  paymentConfirmed.value = false
  settleVisible.value = true
  await nextTick()
  settleFormRef.value?.clearValidate()
}
const beforeProofUpload = (file: UploadRawFile) => {
  if (!['image/jpeg', 'image/png'].includes(file.type) || file.size > 5 * 1024 * 1024) {
    ElMessage.warning('请上传不超过 5MB 的 JPG 或 PNG 图片')
    return false
  }
  return true
}
const uploadProof = async (options: UploadRequestOptions) => {
  if (!settleRecord.value?.id) throw new Error('请重新打开结算单')
  uploading.value = true
  try {
    const file = await LiveApi.uploadLiveSettleProof(settleRecord.value.id, options.file)
    settleForm.proofFileId = file.id
    proofUrl.value = file.url
    await settleFormRef.value?.validateField('proofFileId')
    return file
  } finally {
    uploading.value = false
  }
}
const submitSettle = async () => {
  if (saving.value || uploading.value || !paymentConfirmed.value || !settleRecord.value?.id) return
  await settleFormRef.value?.validate()
  if (new Date(settleForm.paidAt.replace(/-/g, '/')).getTime() > Date.now()) {
    ElMessage.warning('付款时间不能晚于当前时间')
    return
  }
  try {
    await ElMessageBox.confirm(
      `确认已向 ${settleRecord.value.anchorNickname || '该主播'} 转账 ${settleRecord.value.commissionAmount} 元？登记后不能再次结算。`,
      '确认线下付款',
      { type: 'warning' }
    )
  } catch {
    return
  }
  saving.value = true
  try {
    await LiveApi.confirmLiveOfflineSettle({
      id: settleRecord.value.id,
      amount: Number(settleRecord.value.commissionAmount),
      paymentRef: settleForm.paymentRef.trim(),
      proofFileId: settleForm.proofFileId!,
      paidAt: settleForm.paidAt,
      remark: settleForm.remark
    })
    ElMessage.success('线下付款已登记')
    settleVisible.value = false
    await getList()
    if (detailVisible.value && detail.value?.id === settleRecord.value.id)
      await openDetail(settleRecord.value.id)
  } finally {
    saving.value = false
  }
}

onMounted(getList)
</script>
